import { useMemo } from 'react';
import { useBooleanConditions } from '../../../../hooks/useBooleanConditions.tsx';
import { type IWidgetOption } from '../../../../Widget.ts';
import {
  type IComponentOptions,
  WidgetOptionLogic,
} from '../../../../WidgetOptionLogic.ts';
import { type ISingleSelectWidget } from '../ISingleSelectWidget.ts';

interface IUseSingleSelectWidgetInput {
  widget: ISingleSelectWidget;
  name: string;
  preview?: boolean;
}

export interface IUseSingleSelectWidgetOptionsReturn {
  options: IWidgetOption[];
  componentOptions: IComponentOptions;
}

/**
 * Ottieniamo tutte le option e le relative condizioni di visibilità/abilitazione
 * per le select singole
 */
export function useSingleSelectWidgetOptions({
  widget,
  name,
  preview,
}: IUseSingleSelectWidgetInput): IUseSingleSelectWidgetOptionsReturn {
  const optionsWithConditions = useMemo(() => {
    return widget.options.map(option => ({
      option,
      visible: option.visible,
      enabled: option.type === 'divider' ? null : option.enabled,
    }));
  }, [widget.options]);

  const visibles = useBooleanConditions(
    optionsWithConditions.map(o => o.visible),
    { name, preview },
  );

  const enableds = useBooleanConditions(
    optionsWithConditions.map(o => o.enabled),
    { name, preview },
  );

  return useMemo(() => {
    const options = widget.options
      .map((option, i) => ({
        ...option,
        disabled: !enableds[i].result,
      }))
      .filter((_, index) => visibles[index].result);

    return {
      options,
      componentOptions: WidgetOptionLogic.parseComponentOptions(options),
    };
  }, [widget.options, visibles, enableds]);
}
